/**
 * IdeasPage Component
 *
 * Main Ideas page with search, AI picks, market mood, market map, and screener preview.
 */

import { FC, useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { Link, useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { colors, spacing, fontStyles } from '../../styles';
import Page from '../Page';
import StickyHeader from '../StickyHeader';
import Search from '../Search';
// eslint-disable-next-line import/no-named-as-default
import LatestTradesCard from '../LatestTradesCard';
import { PerformanceCards } from '../AIArena';
import CollectionCard from '../CollectionCard';
import { getCollectionsList } from '../../apis/backend';
import { getCollectionImages } from '../../utils/getImages';
import * as stores from '../../stores';
import Button from '../Button';
import FeedbackFooter from '../FeedbackFooter';
import { FREE_COLLECTIONS_LIMIT } from '../../constants/freemium';

// Featured collections to show in the preview (first from each key section)
const FEATURED_SECTION_ORDER = [
  "Don't know where to get started?",
  'Follow trends',
  'Chase high flyers',
  'Leaderboards',
  'Follow famous investors',
  'Invest in value',
  'Find beat-up stocks',
];

const IdeasPage: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [imageMap, setImageMap] = useState<Record<string, string>>({});
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const hasActiveSubscription = !!purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;

  // Fetch collections data
  const collectionsQuery = useQuery(['investments', 'collections'], getCollectionsList);

  // Get featured collections (Bloom Portfolio first, then first from each key section).
  // Deduplicate by id (keeping first occurrence) in case Bloom Portfolio also belongs
  // to one of the featured sections — duplicates would corrupt the paywall index map.
  const featuredCollections = useMemo(() => {
    if (!collectionsQuery.data) return [];
    const all = [
      // Bloom Portfolio first
      ...(collectionsQuery.data || []).filter((c) => c.slug === 'the-bloom-portfolio').slice(0, 1),
      // Then first from each section
      ...FEATURED_SECTION_ORDER.flatMap((section) =>
        (collectionsQuery.data || []).filter((c) => c.section === section).slice(0, 1),
      ),
    ];
    // Remove duplicates, preserving first-seen order so indices stay stable
    const seen = new Set<number>();
    return all.filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [collectionsQuery.data]);

  // Pre-compute global index within the featured row for paywall logic.
  // IdeasPage shows one horizontal row of featured collections; FREE_COLLECTIONS_LIMIT
  // applies to that row as a whole (first N cards are free, the rest are locked).
  const collectionIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    featuredCollections.forEach((collection, index) => {
      map.set(collection.id, index);
    });
    return map;
  }, [featuredCollections]);

  // Load collection images
  useEffect(() => {
    const updateImages = () => {
      const newImageMap: Record<string, string> = {};
      collectionsQuery.data?.forEach((collection) => {
        const imageSrc = getCollectionImages(collection.imageId);
        if (imageSrc && collection.imageId) {
          newImageMap[collection.imageId] = imageSrc;
        }
      });
      setImageMap(newImageMap);
    };

    updateImages();
    window.addEventListener('themeChanged', updateImages);
    return () => window.removeEventListener('themeChanged', updateImages);
  }, [collectionsQuery.data]);

  const handleBrowseCollections = () => {
    history.push('/ideas/collections');
  };

  return (
    <Page showNavigationBar>
      <StickyHeader>
        <h1
          css={css`
            font-size: 1.38rem;
            line-height: 1.88rem;
            font-weight: 500;
            margin: 0;
            color: var(--text-color);
          `}
        >
          {t('ideas.title')}
        </h1>
      </StickyHeader>

      {/* Search Bar */}
      <Search
        isAllowBookmark
        autoFocus={false}
        css={css`
          margin-bottom: ${spacing.regular};
          position: relative;
          z-index: 20;
        `}
      />

      <div
        css={css`
          max-width: 600px;
          margin: 0 auto;
        `}
      >
        {/* Section 1: Latest Trades */}
        <section>
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: ${spacing.centi};
            `}
          >
            <h2
              css={css`
                ${fontStyles.lion}
                margin: 0;
                color: var(--text-color);
              `}
            >
              {t('ideas.latestTrades')}
            </h2>
            <Link
              to="/ideas/picks"
              css={css`
                ${fontStyles.mouse}
                color: ${colors.petrolBlue};
                text-decoration: none;

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              {t('ideas.seeAllTrades')}
            </Link>
          </div>
          <LatestTradesCard />
        </section>

        {/* Section 2: AI Portfolio Competition */}
        <section
          css={css`
            margin-top: ${spacing.hecto};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: ${spacing.centi};
            `}
          >
            <h2
              css={css`
                ${fontStyles.lion}
                margin: 0;
                color: var(--text-color);
              `}
            >
              {t('ideas.copyTrade')}
            </h2>
            <Link
              to="/ideas/ai-arena"
              css={css`
                ${fontStyles.mouse}
                color: ${colors.petrolBlue};
                text-decoration: none;

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              {t('ideas.seeDetails')}
            </Link>
          </div>
          <PerformanceCards onCardClick={() => history.push('/ideas/ai-arena')} />
        </section>

        {/* Section 3: Collections */}
        <section
          css={css`
            margin-top: ${spacing.hecto};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: ${spacing.centi};
            `}
          >
            <h2
              css={css`
                ${fontStyles.lion}
                margin: 0;
                color: var(--text-color);
              `}
            >
              {t('ideas.collections')}
            </h2>
            <button
              onClick={handleBrowseCollections}
              css={css`
                background: none;
                border: none;
                cursor: pointer;
                ${fontStyles.mouse}
                color: ${colors.petrolBlue};
                padding: ${spacing.micro} 0;

                &:hover {
                  text-decoration: underline;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
            >
              {t('ideas.seeAllCollections')}
            </button>
          </div>

          {/* Horizontal scrolling collection cards */}
          <div
            css={css`
              display: flex;
              overflow-x: auto;
              gap: ${spacing.centi};
              padding: ${spacing.micro} 0;
              margin-right: -${spacing.deci};
              padding-right: ${spacing.deci};
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
              &::-webkit-scrollbar {
                display: none;
              }
            `}
          >
            {featuredCollections.map((collection) => {
              const imageSrc = imageMap[collection.imageId ?? ''];

              // Paywall: locked if beyond free limit OR restricted (unless subscribed)
              const rowIndex = collectionIndexMap.get(collection.id) ?? Infinity;
              const isRestricted = collection.restrictedToSubscribers && !hasActiveSubscription;
              const isBeyondFreeLimit = !hasActiveSubscription && rowIndex >= FREE_COLLECTIONS_LIMIT;
              const isLocked = isRestricted || isBeyondFreeLimit;

              const cardComponent = (
                <CollectionCard
                  name={t(`collections.names.${collection.slug}`, { defaultValue: collection.name })}
                  imageSrc={imageSrc}
                  isLocked={isLocked}
                />
              );

              return (
                <div
                  key={collection.id}
                  css={css`
                    flex: 0 0 auto;
                    width: 120px;
                    position: relative;

                    &::before {
                      content: '';
                      padding-bottom: 120%;
                      display: block;
                    }
                  `}
                >
                  <div
                    css={css`
                      position: absolute;
                      top: 0;
                      height: 100%;
                      width: 100%;
                    `}
                  >
                    {isLocked ? (
                      <Button
                        onClick={() => stores.showPaymentModalByPreference('ideas_page_collection')}
                        css={css`
                          height: 100%;
                          width: 100%;
                        `}
                      >
                        {cardComponent}
                      </Button>
                    ) : (
                      <Link to={`/collection/${collection.id}`}>{cardComponent}</Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <FeedbackFooter />
      </div>
    </Page>
  );
};

export default IdeasPage;
